import { useNavigate } from '@tanstack/react-router';
import { isToday, isYesterday } from 'date-fns';
import { Bell, CalendarX, CheckSquare, FileText, FileWarning, Mail, RefreshCw, UserCheck, UserCog, UserMinus, UserPlus, UserX } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTimezone } from '@/components/contexts/TimezoneContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import useNotifications, { type Notification } from '@/hooks/useNotifications';
import { bundleBaseForRegion, bundlePaths } from '@/lib/bundleRoutes';
import { formatSmartDate } from '@/utils/date-utils';


export default function NotificationCenter() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const timezone = getTimezone();

  const handleNotificationClick = (notification: Notification) => {
    // Mark notification as read
    markAsRead(notification.id);

    // If notification type is request_rejected or removed_from_matter, don't navigate anywhere
    if (['request_rejected', 'removed_from_matter'].includes(notification.notification_type)) {
      setOpen(false);
      return;
    }

    // If it has a matter_guid, navigate to the appropriate page based on notification type
    if (notification.matter_guid) {
      const slug = notification.matter_guid;
      if (['added_to_matter', 'request_approved'].includes(notification.notification_type)) {
        navigate({ to: '/matter/$slug/case-files/correspondence', params: { slug } });
        setOpen(false);
      } else if (['membership_request', 'role_updated', 'member_left_matter'].includes(notification.notification_type)) {
        navigate({ to: '/matter/$slug/members', params: { slug } });
        setOpen(false);
      } else if (['task_assigned', 'calendar_sync_deleted', 'calendar_sync_reverted'].includes(notification.notification_type)) {
        // Navigate to deadlines page for task assignment and calendar sync notifications
        pushDeadlines(notification.matter_guid, getNotificationTaskId(notification));
      } else if (notification.notification_type === 'bundle_files_removed') {
        const base = bundlePaths(bundleBaseForRegion(notification.region));
        navigate({ to: base.index, params: { slug } });
        setOpen(false);
      } else if (['sync_paused', 'imanage_sync_paused'].includes(notification.notification_type)) {
        openMatterSyncs(notification);
      } else if (notification.notification_type === 'draft_generated') {
        openDraft(notification);
      }
    }
  };

  const openDraft = (notification: Notification) => {
    const documentGuid = getNotificationDraftId(notification);
    if (!notification.matter_guid || !documentGuid) return;

    markAsRead(notification.id);
    navigate({
      to: '/matter/$slug/drafting/$documentGuid',
      params: { slug: notification.matter_guid, documentGuid },
    });
    setOpen(false);
  };

  const openMatterSyncs = (notification: Notification) => {
    if (!notification.matter_guid) return;

    markAsRead(notification.id);
    navigate({ to: '/matter/$slug/sync', params: { slug: notification.matter_guid } });
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    // When opening the notification center, mark all unread notifications as read
    if (newOpen && unreadCount > 0) {
      // Mark all unread notifications as read
      notifications.forEach(notification => {
        if (!notification.is_read) {
          markAsRead(notification.id);
        }
      });
    }
    setOpen(newOpen);
  };

  const formatTime = (dateString: string) => {
    return formatSmartDate(dateString, timezone);
  };

  const NOTIFICATION_TITLE_I18N_KEYS = {
    membership_request: 'membership-request',
    added_to_matter: 'added-to-matter',
    request_approved: 'request-approved',
    request_rejected: 'request-rejected',
    role_updated: 'role-updated',
    removed_from_matter: 'removed-from-matter',
    member_left_matter: 'member-left-matter',
    task_assigned: 'task-assigned',
  } as const;

  const NOTIFICATION_TITLE_FALLBACKS: Record<string, string> = {
    calendar_sync_deleted: 'Calendar Sync Removed',
    calendar_sync_reverted: 'Outlook Changes Reverted',
    bundle_files_removed: 'Files Removed',
    sync_paused: 'Sync Paused',
    // Legacy value for notifications created before the provider-agnostic rename.
    imanage_sync_paused: 'Sync Paused',
    draft_generated: 'Draft Ready',
  };

  const getNotificationTitle = (type: string) => {
    const i18nKey = NOTIFICATION_TITLE_I18N_KEYS[type as keyof typeof NOTIFICATION_TITLE_I18N_KEYS];
    if (i18nKey) return t(i18nKey);
    return NOTIFICATION_TITLE_FALLBACKS[type] ?? type;
  };

  const getNotificationTaskId = (notification: Notification) => {
    if (notification.notification_type === 'calendar_sync_reverted') {
      return notification.message.match(/#-([a-f0-9-]+)-#/)?.[1] ?? null;
    }

    if (['task_assigned', 'calendar_sync_deleted'].includes(notification.notification_type)) {
      return notification.message.match(/\(#([a-f0-9-]+)\)/)?.[1] ?? null;
    }

    return null;
  };

  const getNotificationDraftId = (notification: Notification) => (
    notification.message.match(/\[draft:([^\]]+)\]/)?.[1] ?? null
  );

  const pushDeadlines = (matterGuid: string, taskId?: string | null) => {
    navigate({
      to: '/matter/$slug/deadlines',
      params: { slug: matterGuid },
      search: taskId ? { taskId } : {},
    });
    setOpen(false);
  };

  const groupNotificationsByDate = () => {
    const grouped: Record<string, Notification[]> = {};
    
    notifications.forEach((notification) => {
      const date = new Date(notification.created_at);
      let dateGroup = 'Older';
      
      if (isToday(date)) {
        dateGroup = 'Today';
      } else if (isYesterday(date)) {
        dateGroup = 'Yesterday';
      }
      
      if (!grouped[dateGroup]) {
        grouped[dateGroup] = [];
      }
      
      grouped[dateGroup].push(notification);
    });
    
    return grouped;
  };

  const groupedNotifications = groupNotificationsByDate();

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'membership_request':
        return <UserPlus className="h-4 w-4" />;
      case 'added_to_matter':
        return <UserCheck className="h-4 w-4" />;
      case 'request_approved':
        return <UserCheck className="h-4 w-4" />;
      case 'request_rejected':
        return <UserX className="h-4 w-4" />;
      case 'role_updated':
        return <UserCog className="h-4 w-4" />;
      case 'removed_from_matter':
        return <UserMinus className="h-4 w-4" />;
      case 'member_left_matter':
        return <UserMinus className="h-4 w-4" />;
      case 'task_assigned':
        return <CheckSquare className="h-4 w-4" />;
      case 'calendar_sync_deleted':
        return <CalendarX className="h-4 w-4" />;
      case 'calendar_sync_reverted':
        return <RefreshCw className="h-4 w-4" />;
      case 'bundle_files_removed':
        return <FileWarning className="h-4 w-4" />;
      case 'sync_paused':
      case 'imanage_sync_paused':
        return <RefreshCw className="h-4 w-4" />;
      case 'draft_generated':
        return <FileText className="h-4 w-4" />;
      default:
        return <Mail className="h-4 w-4" />;
    }
  };

  const renderNotificationMessage = (notification: Notification) => {
    // For calendar_sync_reverted, parse both the GUID and Outlook URL
    if (notification.notification_type === 'calendar_sync_reverted' && notification.matter_guid) {
      const matterGuid = notification.matter_guid;
      const guidMatch = notification.message.match(/#-([a-f0-9-]+)-#/);
      const outlookMatch = notification.message.match(/\[outlook:([^\]]+)\]/);

      if (guidMatch) {
        const taskId = guidMatch[1];
        const messageWithoutMarkers = notification.message
          .replace(/#-[a-f0-9-]+-#/, '')
          .replace(/\s*\[outlook:[^\]]+\]/, '');
        const outlookUrl = outlookMatch ? outlookMatch[1] : null;

        return (
          <>
            {messageWithoutMarkers}{' '}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                pushDeadlines(matterGuid, taskId);
              }}
              className="text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0"
            >
              Open deadlines
            </button>
            {outlookUrl && (
              <>
                {' | '}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    // outlookUrl is now just the event ID, not the full URL
                    const webUrl = `https://outlook.office365.com/calendar/item/${encodeURIComponent(outlookUrl)}`;
                    const desktopUrl = `ms-outlook://view/calendar/?itemid=${encodeURIComponent(outlookUrl)}`;

                    // Try desktop app first
                    window.location.href = desktopUrl;

                    // Fallback to web after delay
                    setTimeout(() => {
                      window.open(webUrl, '_blank', 'noopener,noreferrer');
                    }, 500);
                  }}
                  className="text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0"
                >
                  Open in Outlook
                </button>
              </>
            )}
          </>
        );
      }
    }

    // For task assignments and calendar sync deletions, parse out the GUID and make it a link
    if (['task_assigned', 'calendar_sync_deleted'].includes(notification.notification_type) && notification.matter_guid) {
      const matterGuid = notification.matter_guid;
      const guidMatch = notification.message.match(/\(#([a-f0-9-]+)\)/);
      if (guidMatch) {
        const taskId = guidMatch[1];
        const messageWithoutGuid = notification.message.replace(/\s*\(#[a-f0-9-]+\)/, '');

        return (
          <>
            {messageWithoutGuid}{' '}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                pushDeadlines(matterGuid, taskId);
              }}
              className="text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0"
            >
              Open deadlines
            </button>
          </>
        );
      }
    }
    if (['sync_paused', 'imanage_sync_paused'].includes(notification.notification_type) && notification.matter_guid) {
      // A paused sync has several possible causes (reauth, an invalid root folder,
      // a deleted source folder, ...) with different fixes, so route to Folder
      // Syncing - which shows each sync's reason and the right action - rather than
      // assuming a reconnect. Older (pre-generic) messages appended their own
      // "Open Folder Syncing ..." call to action; strip any such trailing sentence
      // so we render a single, provider-agnostic link below.
      const message = notification.message.replace(/\s*Open Folder Syncing\b[^.]*\.?\s*$/, '');

      return (
        <>
          {message}{' '}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openMatterSyncs(notification);
            }}
            className="cursor-pointer border-none bg-transparent p-0 text-blue-600 hover:underline"
          >
            Open Folder Syncing
          </button>
          .
        </>
      );
    }
    if (notification.notification_type === 'draft_generated' && notification.matter_guid) {
      return (
        <>
          {notification.message.replace(/\s*\[draft:[^\]]+\]/, '')}{' '}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openDraft(notification);
            }}
            className="cursor-pointer border-none bg-transparent p-0 text-blue-600 hover:underline"
          >
            Open draft
          </button>
        </>
      );
    }
    return notification.message;
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-slate-600 hover:text-slate-900">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge className="absolute bg-crimson -top-0.5 -right-0.5 px-1 min-w-4 h-4 flex items-center justify-center text-xs">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="p-3 pl-5 border-b border-border">
          <h3 className="font-medium">{t('notifications')}</h3>
        </div>
        <ScrollArea className="h-[400px] px-3 py-2">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              {t('no-notifications')}
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedNotifications).map(([dateGroup, notifications]) => (
                <div key={dateGroup}>
                  <h4 className="text-sm font-medium mb-2 pl-2">{dateGroup}</h4>
                  <div className="space-y-2">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-3 rounded-md cursor-pointer transition-colors ${
                          notification.is_read ? 'bg-background hover:bg-muted/50' : 'bg-muted hover:bg-muted/70'
                        }`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex items-center gap-2">
                            {getNotificationIcon(notification.notification_type)}
                            <span className="text-sm font-medium">
                              {getNotificationTitle(notification.notification_type)}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {formatTime(notification.created_at)}
                          </span>
                        </div>
                        <p className="text-sm">{renderNotificationMessage(notification)}</p>
                        {notification.matter_name && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {notification.matter_name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <Separator className="my-2" />
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
